<template>
  <div id="service-documents">
    <v-row v-if="isLoading" no-gutters>
      <v-col cols="12">
        <div style="width: 100%; height: 100%; position: absolute; z-index: 1"></div>
      </v-col>
    </v-row>
    <div v-else ref="capture">
      <v-row no-gutters style="margin-bottom: 30px">
        <v-col cols="12">
          <div></div>
          <h3 class="document-title">즉시입금 약정서</h3>
          <div class="document-information">
            본인은 아래 서비스 제공자와 아래의 조건에 따라 
            여신거래를 함에 있어 약정서에 기재된 사항이
            본인의 자필임을 확인하며, 즉시입금 서비스 이용을 아래와 같이 약정합니다. 
          </div>
        </v-col>

        <v-col cols="12" class="py-5">
          <v-expansion-panels v-model="panel" multiple flat>
            <v-expansion-panel
              v-for="document in documents"
              :key="document.title"
              :ref="document.name"
              :class="{ camera: isCameraMode }"
            >
              <v-expansion-panel-header class="panel-header">
                <template v-slot:default>
                  <div class="d-flex align-center">
                    <div class="panel-header__checkbox">
                      <div :class="{ 'is-agree': document.isAgreed }">
                        <img class="cen" src="@/assets/images/icons/check.svg" />
                      </div>
                    </div>
                    <p class="panel-header__title">
                      {{ document.title }}
                    </p>
                  </div>
                </template>
                <template v-slot:actions>
                  <img class="v-icon" src="@/assets/images/icons/arrow-down.svg" />
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="py-3 px-0">
                <component
                  :is="document.component"
                  v-bind="{ cameraMode: isCameraMode }"
                  @emitAgreed="(v) => agreeDoc(document, v)"
                  @emitUserSign="(v) => emitUserSign(v)"
                >
                </component>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </div>
    <v-row no-gutters>
      <v-col cols="12" class="complete">
        <button
          :v-ripple="!btnDisabled"
          :disabled="btnDisabled"
          :class="{ active: !btnDisabled }"
          @click="nextStep()"
        >
          <template v-if="nextLoading">
            <v-progress-circular size="24" indeterminate color="primary"></v-progress-circular>
          </template>
          <template v-else>다음</template>
        </button>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import { sleep } from '@/util'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  components: {
    DocumentCreditor: () => import('@/components/documents/DocumentCreditor.vue'),
    DocumentDebtor: () => import('@/components/documents/DocumentDebtor.vue'),
    DocumentLoanInformation: () => import('@/components/documents/DocumentLoanInformation.vue'),
    DocumentAccount: () => import('@/components/documents/DocumentAccount.vue'),
    DocumentCreditInquiry: () => import('@/components/documents/DocumentCreditInquiry.vue'),
    DocumentAttorney: () => import('@/components/documents/DocumentAttorney.vue'),
    DocumentLimitLoan: () => import('@/components/documents/DocumentLimitLoan.vue'),
  },
  data() {
    return {
      origin: {
        contract: null,
        term: null,
      },
      contractImage: {
        signature: null,
        image: null,
      },
      showTemp: false,
      isLoading: false,
      isCameraMode: false,
      nextLoading: false,
      panel: [1],
      documents: [
        {
          name: 'DocumentCreditor',
          title: '여신거래 서비스 제공자 정보',
          isAgreed: true,
          component: 'DocumentCreditor',
        },
        {
          name: 'DocumentDebtor',
          title: '이용자 정보(본인)',
          isAgreed: false,
          component: 'DocumentDebtor',
        },
        {
          name: 'DocumentLoanInformation',
          title: '여신거래 정보',
          isAgreed: false,
          component: 'DocumentLoanInformation',
        },
        {
          name: 'DocumentAccount',
          title: '즉시입금 지급 및 매출 반환계좌',
          isAgreed: false,
          component: 'DocumentAccount',
        },
        {
          name: 'DocumentCreditInquiry',
          title: '개인(신용)정보 조회 동의서',
          isAgreed: false,
          component: 'DocumentCreditInquiry',
        },
        {
          name: 'DocumentAttorney',
          title: '채권양도승낙서 및 위임장',
          isAgreed: false,
          component: 'DocumentAttorney',
        },
        {
          name: 'DocumentLimitLoan',
          title: '여신한도거래 약관 동의',
          isAgreed: false,
          component: 'DocumentLimitLoan',
        },
      ],
    }
  },
  computed: {
    ...mapState('contract', ['limitTransaction']),
    isAgreedAll() {
      return this.documents.every((d) => {
        return d.isAgreed === true
      })
    },
    btnDisabled() {
      return this.nextLoading === true || this.isAgreedAll === false
    },
    registerContractTerm() {
      return {
        contract: this.origin.contract?.uuid,
        term: this.origin.term?.uuid,
        isAgreed: true,
        signature: this.contractImage.signature, // 사인
        image: this.contractImage.image, // 계약서 전체
      }
    },
    ...mapGetters('myfinbiz', ['platformName']),
  },
  async created() {
    if (!this.limitTransaction) {
      await this.getLimitTransaction()
    }
    const limitTransaction = this.limitTransaction
    const hasContract = await this.checkExistContract(limitTransaction)
    if (hasContract) {
      // return this.$emit('emitResult', true)
      return this.$router.push('/earlypay')
    }
    // 기본계약서
    this.origin.contract = await this.getContractCurrent()
    // 여신한도거래계약서
    this.origin.term = await this.getContractTermsCode({ code: 'LIMIT_TRANSACTION' })
  },
  methods: {
    ...mapActions('agreement', [
      'getContractAgreements',
      'getContractCurrent',
      'getContractTermsCode',
      'addContractAgreements',
    ]),
    ...mapActions('loan', ['getLoansExecuteFunding']),
    ...mapActions('contract', ['getLimitTransaction', 'checkExistContract']),
    openAllPanels() {
      this.panel = this.documents.map((k, i) => i)
    },
    agreeDoc(document, v) {
      document.isAgreed = v
      if (v === true) {
        const current = this.documents.findIndex((doc) => doc.name === document.name)
        const pan = this.panel.indexOf(current)
        if (pan > -1 && current < this.documents.length - 1) {
          this.panel.splice(pan, 1)
          this.panel.push(current + 1)

          this.$refs[document.name][0].$el.scrollIntoView({ behavior: 'smooth' })
        }
      }
    },
    capture() {
      const result = new Promise(async (resolve, reject) => {
        const element = this.$refs.capture
        const canvas = await html2canvas(element, {
          x: 0,
          y: 0,
          scale: 1,
        })
        canvas.toBlob(resolve)
      })
      return result
    },
    async nextStep() {
      if (this.btnDisabled) {
        return
      }

      try {
        this.nextLoading = true
        this.isCameraMode = true

        this.openAllPanels()
        await sleep(1500)

        if (this.panel.length === this.documents.length) {
          this.contractImage.image = await this.capture()
        }
        await this.addContractAgreements(this.registerContractTerm)
        this.$emit('emitResult', true)
      } finally {
        this.isCameraMode = false
        this.nextLoading = false
      }
    },
    emitUserSign(v) {
      this.contractImage.signature = v
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common/_variables.scss';

#service-documents {
  display: block;
  width: 100%;

  background-color: $white;
  box-sizing: border-box;

  .document-title {
    font-weight: 900;
    text-align: center;

    margin-top: 20px;
    margin-bottom: 20px;
  }

  .document-information {
    background-color: #f4faff;
    color: #333333;
    font-size: 16px;
    line-height: 1.5;

    padding: 20px 36px;
  }

  .v-expansion-panel {
    background-color: #f4faff;
    border-radius: $border-radius-default !important;

    &:not(:first-child) {
      margin-top: 16px;
    }

    &:not(.camera):before {
      box-shadow: 0 6px 16px 0 rgba(32, 35, 36, 0.08) !important;
    }

    &:not(.camera) {
      .v-expansion-panel-header--active {
        box-shadow: 0 6px 16px 0 rgba(32, 35, 36, 0.08) !important;
      }
    }

    &.v-expansion-panel--active::before {
      box-shadow: unset !important;
    }
  }

  .v-expansion-panel-content {
    background-color: white;
  }

  :deep(.v-expansion-panel-content__wrap) {
    padding: 0;
  }

  .panel-header {
    background-color: #f4faff;
    height: 60px;

    &__checkbox {
      width: 52px;

      > div {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 24px;
        height: 24px;

        background-color: #d7e1ea;
        border-radius: 100%;

        &.is-agree {
          background-color: $main-color-default;
        }
      }

      img {
        object-fit: contain;
      }
    }

    &__title {
      color: #8a94a2;
      font-size: 15px !important;
      font-weight: 900 !important;
      white-space: nowrap;
      word-wrap: break-word;

      margin: 0;
      padding-left: 8px;
    }
  }

  .complete {
    position: relative;

    button {
      width: 100%;
      max-width: $standard-max-width;
      height: 68px;
      bottom: 0;
      color: #727d8c;
      font-weight: 900;
      font-size: 22px;
      text-align: center;
      background-color: #e3ecf6;
      z-index: 100;

      &.active {
        color: $white;
        background-color: $main-color-default;
      }
    }
  }

  :deep {
    .agree {
      p {
        font-weight: 600;
        font-size: 16px;
        line-height: 48px;
      }

      button {
        width: 80px;
        height: 48px;
        border-radius: 4px;
        border: solid 1px #d7e1ea;
        background-color: $white;
        color: #727d8c;
        font-size: 15px;
        line-height: 1.6;
      }
    }
  }

  :deep(button.active) {
    background-color: $main-color-default;
    border-color: $main-color-default;
    color: $white;
    font-weight: bold;
  }
}
</style>
